import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { userAPI, batchAPI, notificationAPI } from '../../utils/api';
import { DashboardLayout } from '../../components/layout';
import { Spinner } from '../../components/ui';
import toast from 'react-hot-toast';

export default function Dashboard() {
  const nav = useNavigate();
  const [data,    setData]    = useState(null);
  const [batches, setBatches] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      userAPI.getDashboard(),
      batchAPI.getAll(),
    ]).then(([dash, batch]) => {
      setData(dash.data);
      setBatches(batch.data);
    }).catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <DashboardLayout title="Dashboard">
      <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:400 }}>
        <Spinner size={40} color="blue" />
      </div>
    </DashboardLayout>
  );

  const user     = data?.user || {};
  const stats    = data?.stats || {};
  const recent   = data?.recentTests || [];
  const summary  = batches?.summary || {};
  const nextBatch = summary.nextBatch;
  const allBatches = batches?.batches || [];
  const availableBatches = allBatches.filter(b => b.status === 'unlocked');

  const pct = summary.total ? Math.round((summary.completed / summary.total) * 100) : 0;

  return (
    <DashboardLayout title={`Welcome back, ${user.full_name?.split(' ')[0]} 👋`} subtitle="Your TNPSC preparation dashboard">

      {/* ── Available Batch Alert ── */}
      {availableBatches.length > 0 && (
        <div style={{
          background:'linear-gradient(135deg,var(--blue),var(--blue-mid))',
          borderRadius:16, padding:'18px 22px', marginBottom:20,
          display:'flex', alignItems:'center', justifyContent:'space-between', gap:12,
          color:'#fff',
        }}>
          <div>
            <div style={{ fontFamily:'var(--font-heading)', fontWeight:900, fontSize:18, marginBottom:3 }}>
              🔓 Batch {availableBatches[0].batch_number} is Ready!
              {availableBatches[0].batch_number === 100 && ' 🏆'}
            </div>
            <div style={{ fontSize:13, opacity:0.9 }}>
              {availableBatches[0].total_questions} questions · {availableBatches[0].duration_minutes} mins timer · Click to start now
            </div>
          </div>
          <button
            onClick={() => nav(`/dashboard/batch/${availableBatches[0].id}/test`)}
            style={{ background:'rgba(255,255,255,0.2)', border:'2px solid rgba(255,255,255,0.5)', color:'#fff', borderRadius:12, padding:'10px 22px', fontWeight:700, fontSize:14, cursor:'pointer', whiteSpace:'nowrap' }}>
            Start Test →
          </button>
        </div>
      )}

      {/* ── Stats Cards ── */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:14, marginBottom:20 }}>
        {[
          { label:'Batches Done',    value: summary.completed || 0,               icon:'✅', color:'#28A745', sub:`of ${summary.total||100}` },
          { label:'Tests Taken',     value: stats.total_tests || 0,               icon:'📝', color:'var(--blue)' },
          { label:'Avg Score',       value: stats.avg_score ? parseFloat(stats.avg_score).toFixed(1)+'%' : '—', icon:'🎯', color:'#F5A623' },
          { label:'Best Score',      value: stats.best_score ? parseFloat(stats.best_score).toFixed(1)+'%' : '—', icon:'🌟', color:'#8B5CF6' },
        ].map(s => (
          <div key={s.label} style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:14, padding:'18px 16px' }}>
            <div style={{ fontSize:26, marginBottom:6 }}>{s.icon}</div>
            <div style={{ fontFamily:'var(--font-heading)', fontWeight:900, fontSize:26, color:s.color }}>{s.value}</div>
            <div style={{ fontSize:12, color:'var(--muted)', marginTop:2 }}>{s.label}</div>
            {s.sub && <div style={{ fontSize:11, color:'var(--muted)' }}>{s.sub}</div>}
          </div>
        ))}
      </div>

      {/* ── Progress Bar ── */}
      <div style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:16, padding:'20px 22px', marginBottom:20 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
          <div style={{ fontFamily:'var(--font-heading)', fontWeight:700, fontSize:16 }}>Overall Batch Progress</div>
          <div style={{ fontFamily:'var(--font-heading)', fontWeight:900, fontSize:20, color:'var(--blue)' }}>
            {summary.completed||0} / {summary.total||100}
          </div>
        </div>
        <div style={{ background:'var(--border)', borderRadius:99, height:14, overflow:'hidden', marginBottom:10 }}>
          <div style={{
            height:'100%', borderRadius:99,
            background:'linear-gradient(90deg,var(--blue),var(--blue-mid))',
            width:`${pct}%`, transition:'width 0.8s ease',
          }} />
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', fontSize:12 }}>
          <span style={{ color:'#28A745', fontWeight:600 }}>✅ {summary.completed||0} completed</span>
          <span style={{ color:'var(--blue)', fontWeight:600 }}>🔓 {summary.available||0} available</span>
          <span style={{ color:'var(--muted)' }}>🔒 {summary.locked||0} locked</span>
        </div>
        <button className="btn btn-primary" style={{ marginTop:14, width:'100%' }}
          onClick={() => nav('/dashboard/tests')}>
          View All Batches →
        </button>
      </div>

      {/* ── Recent Results ── */}
      {recent.length > 0 && (
        <div style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:16, padding:'20px 22px' }}>
          <div style={{ fontFamily:'var(--font-heading)', fontWeight:700, fontSize:16, marginBottom:16 }}>
            Recent Tests 📊
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {recent.map(r => (
              <div key={r.id} style={{
                display:'flex', alignItems:'center', gap:12, padding:'12px 14px',
                background:'var(--bg)', borderRadius:12, border:'1px solid var(--border)',
              }}>
                <div style={{
                  width:40, height:40, borderRadius:'50%', flexShrink:0,
                  background:'linear-gradient(135deg,var(--blue),var(--blue-mid))',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  color:'#fff', fontFamily:'var(--font-heading)', fontWeight:900, fontSize:14,
                }}>
                  {r.batch_number}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, fontSize:13, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {r.batch_title || `Batch ${r.batch_number}`}
                  </div>
                  <div style={{ fontSize:11, color:'var(--muted)', marginTop:2 }}>
                    {new Date(r.completed_at).toLocaleDateString('en-IN', { day:'numeric', month:'short' })}
                    · {r.correct}/{r.total_questions} correct
                  </div>
                </div>
                <div style={{ fontFamily:'var(--font-heading)', fontWeight:900, fontSize:18,
                  color: parseFloat(r.percentage)>=60 ? '#28A745' : parseFloat(r.percentage)>=40 ? '#F5A623' : '#dc3545'
                }}>
                  {parseFloat(r.percentage).toFixed(1)}%
                </div>
              </div>
            ))}
          </div>
          <button className="btn btn-outline" style={{ width:'100%', marginTop:14, fontSize:13 }}
            onClick={() => nav('/dashboard/history')}>
            View Full History →
          </button>
        </div>
      )}

      {/* Empty state if no tests taken yet */}
      {recent.length === 0 && summary.completed === 0 && (
        <div style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:16, padding:'32px', textAlign:'center' }}>
          <div style={{ fontSize:56, marginBottom:12 }}>🚀</div>
          <div style={{ fontFamily:'var(--font-heading)', fontWeight:800, fontSize:20, marginBottom:8 }}>Ready to Start?</div>
          <p style={{ color:'var(--muted)', fontSize:14, lineHeight:1.7, maxWidth:340, margin:'0 auto 20px' }}>
            Your first batch is unlocked and waiting! Complete all 100 mock tests to master TNPSC.
          </p>
          <button className="btn btn-primary btn-lg" onClick={() => nav('/dashboard/tests')}>
            Start Batch 1 →
          </button>
        </div>
      )}

    </DashboardLayout>
  );
}
